import { Component } from '@theme/component';
import { Scroller } from '@theme/scrolling';

/**
 * Collection Tabs component
 *
 * Switches between pre-rendered product panels via tab buttons.
 * All panels stay in the layout (visibility: hidden) so that
 * srcset/sizes calculates correct image dimensions.
 * Switching toggles aria-selected on tabs and .is-active on panels.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} nav
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends {Component<Refs>}
 */
class CollectionTabs extends Component {
  requiredRefs = ['nav'];

  /** @type {Scroller} */
  #scroll;

  connectedCallback() {
    super.connectedCallback();
    this.#scroll = new Scroller(this.refs.nav, { onScroll: () => {} });
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scroll?.destroy();
  }

  get tabs() {
    return this.refs.tabs ?? [];
  }

  get panels() {
    return this.refs.panels ?? [];
  }

  get currentIndex() {
    return this.tabs.findIndex(
      (tab) => tab.getAttribute('aria-selected') === 'true'
    );
  }

  /**
   * Select a tab by index.
   * Called via declarative on:click="/selectTab/N"
   *
   * @param {number} index
   * @param {Event} _event
   */
  selectTab(index, _event) {
    this.#activate(index);
  }

  /**
   * Activate a tab and show its panel.
   *
   * @param {number} index
   */
  #activate(index) {
    const { tabs, panels } = this;
    const safeIndex = Math.max(0, Math.min(index, tabs.length - 1));

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === safeIndex;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', String(isActive ? 0 : -1));
    }

    for (const [i, panel] of panels.entries()) {
      panel.classList.toggle('is-active', i === safeIndex);
    }

    const activeTab = tabs[safeIndex];
    if (activeTab) this.#scroll.to(activeTab);
  }

  /**
   * Arrow-key navigation through tabs.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.target.getAttribute('role') !== 'tab') return;

    const navMap = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
    };

    const offset = navMap[event.key];
    if (offset === undefined) return;

    event.preventDefault();

    const current = this.currentIndex;
    const next = (current + offset + this.tabs.length) % this.tabs.length;

    this.tabs[next]?.focus();
    this.#activate(next);
  };
}

if (!customElements.get('collection-tabs-component')) {
  customElements.define('collection-tabs-component', CollectionTabs);
}
